import { useState, useCallback, useMemo, useEffect } from "react";
import { IAdminAuthRequest, AdminAuthRequest } from "../../../entities-generated/models/admin-auth/admin-auth-request";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ValidatedFormValueService, IFormValidityCheck, MutableFormValueService } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged } from "../../../framework/utils";
import { none, where } from "../../../framework/utils/collection";
import { SignInEvents } from "../events";
import { IAdminSignInFormModel, IAdminSignInValueModel, IAdminSignInModelChange } from "./admin-signin-iface";


// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:username/validated/string/string%%
export const username = new (class usernameService extends ValidatedFormValueService<IAdminAuthRequest, string, string> {
    public initialize(data: IAdminAuthRequest): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAdminAuthRequest): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter user name');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'username' // id
    ,'Username' // description
);
// %%END:username/validated/string/string%%

// %%BEGIN:password/validated/string/string%%
export const password = new (class passwordService extends ValidatedFormValueService<IAdminAuthRequest, string, string> {
    public initialize(data: IAdminAuthRequest): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAdminAuthRequest): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter password');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        signInFailed.setInputValue(false);
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'password' // id
    ,'Password' // description
);
// %%END:password/validated/string/string%%

// %%BEGIN:signInFailed/mutable/boolean/boolean%%
export const signInFailed = new (class signInFailedService extends MutableFormValueService<IAdminAuthRequest, boolean, boolean> {
    public initialize(data: IAdminAuthRequest): void {
        this.value = false;
    }

    public applyToDataModel(data: IAdminAuthRequest): void {}

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public setInputValue(value: boolean): void {
        this.value = value;
    }

    public getOutputValue(): boolean {
        return this.value;
    }

    public onUpdate(value: boolean): void {
        this.value = value;
        if(value) { // reset password if sign in failed
            password.setInputValue(password.getDefaultValue());
        }
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'signInFailed' // id
    ,'' // description
);
// %%END:signInFailed/mutable/boolean/boolean%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IAdminSignInFormModel => {
    return {
       username: username.getInputValue(),
       password: password.getInputValue(),
       signInFailed: signInFailed.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IAdminSignInValueModel => {
    return {
       username: username.getOutputValue(),
       password: password.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        username.getInputValue(),
        password.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IAdminSignInModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(SignInEvents.SignInFormModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IAdminSignInFormModel>> => {
    const promises = [
        username.formValidateAndUpdate(),
        password.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IAdminSignInFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IAdminSignInValueModel, dataModel: IAdminAuthRequest } = {
    dataModel: new AdminAuthRequest()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: IAdminAuthRequest) => {
    const dataModel = data || new AdminAuthRequest();

    username.initialize(dataModel);
    password.initialize(dataModel);
    signInFailed.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IAdminSignInFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
username.setChangePublisher(broadcastChange);
password.setChangePublisher(broadcastChange);
signInFailed.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new AdminAuthRequest();

    username.applyToDataModel(dataModel);
    password.applyToDataModel(dataModel);
    signInFailed.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IAdminSignInModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], SignInEvents.SignInFormModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IAdminSignInFormModel => {
    const [getter, setter] = useState<IAdminSignInFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IAdminSignInModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IAdminSignInFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('AdminSignInFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('AdminSignInFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toAdminAuthRequest = () => {
    const v = toValueModel();
    const req: IAdminAuthRequest = {
        password: v.password,
        offset: (new Date()).getTimezoneOffset()
    };
    return req;
}

// ##END:DEVELOPERCODE##
